/* ============================================================================
   HISTORIQUE D'ÉDITION (annuler / rétablir) — module PUR, testable sans DOM
   ----------------------------------------------------------------------------
   Pile de clichés JSON de la scène. `reset(scene)` pose la BASELINE (nouvelle
   génération, ouverture depuis la bibliothèque, schéma vierge) ; `record(scene)`
   ajoute un cliché après chaque geste d'édition validé (commit de editor.js).
   Deux gardes évitent le bruit :
     - un commit sans changement réel (cliché identique) est ignoré ;
     - deux enregistrements très rapprochés (< coalesceMs) fusionnent en UNE
       étape — la frappe « 1 » puis « 12 » dans le champ N° ne fait qu'un undo.
   `undo()` / `redo()` renvoient une COPIE indépendante de la scène (parse du
   cliché) : l'appelant peut la muter sans corrompre la pile.
   ========================================================================== */

export function createHistory({ limit = 60, coalesceMs = 500, now = () => Date.now() } = {}) {
  let stack = [];      // clichés JSON (chaînes), du plus ancien au plus récent
  let idx = -1;        // position courante dans la pile
  let lastPush = -Infinity;

  return {
    /* Nouvelle scène de référence : la pile repart de zéro. `null` = pas de
       scène (écran d'attente) : l'historique est vide et inerte. */
    reset(scene) {
      stack = scene == null ? [] : [JSON.stringify(scene)];
      idx = stack.length - 1;
      lastPush = -Infinity;
    },
    /* Cliché après un geste d'édition. Sans baseline (reset(null)), inerte. */
    record(scene) {
      if (scene == null || idx < 0) return;
      const snap = JSON.stringify(scene);
      if (stack[idx] === snap) return;                     // commit sans effet
      const t = now();
      // Un undo suivi d'une modification COUPE la branche redo.
      stack = stack.slice(0, idx + 1);
      // Fusion des gestes rapprochés (saisie au clavier), jamais sur la baseline.
      if (t - lastPush < coalesceMs && idx > 0) {
        stack[idx] = snap;
      } else {
        stack.push(snap);
        if (stack.length > limit) stack.shift();
        idx = stack.length - 1;
      }
      lastPush = t;
    },
    canUndo: () => idx > 0,
    canRedo: () => idx >= 0 && idx < stack.length - 1,
    undo() { return idx > 0 ? JSON.parse(stack[--idx]) : null; },
    redo() { return idx >= 0 && idx < stack.length - 1 ? JSON.parse(stack[++idx]) : null; },
  };
}
